import { colors } from "@/utils/const";

/*
 * Desktop and mobile are separated with CSS rather than with a JS media query
 * so the server renders the correct bar for every viewport and nothing flashes
 * during hydration. 900px is the boundary the navbar has always used.
 */
export const MOBILE_MEDIA_QUERY = "(max-width:900px)";
export const DESKTOP_MEDIA_QUERY = "(min-width:900.05px)";

export const desktopOnly = { [`@media ${MOBILE_MEDIA_QUERY}`]: { display: "none" } };

export const mobileOnly = { [`@media ${DESKTOP_MEDIA_QUERY}`]: { display: "none" } };

/*
 * The dropdowns render into a portal on <body>, above page sections that go as
 * high as z-index 999999 (see components/steps/stepsComp.js).
 */
export const MENU_Z_INDEX = 1000001;

export const stickyHeader = {
  position: "sticky",
  top: 0,
  zIndex: 111,
  boxShadow: "rgba(0, 0, 0, 0.15) 0px 5px 15px 0px",
};

/* ---------- desktop top bar (grey strip) ---------- */

export const topBar = {
  container: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    color: colors.blue,
    pt: 0.5,
    pb: 0.5,
    bgcolor: colors.light_grey,
  },
  menuButton: {
    minWidth: "50px",
    py: 1,
    justifyContent: "start",
    px: 0,
    "&:hover": { backgroundColor: "transparent" },
    "&:active": {
      backgroundColor: "inherit",
      color: "inherit",
      transition: "none",
    },
  },
  menuIcon: { fontSize: 30, color: colors.blue },
  audienceTab: {
    display: "flex",
    justifyContent: "center",
    marginX: "10px",
    color: colors.blue,
    fontSize: 17,
    borderBottom: `2px solid ${colors.light_grey}`,
    textDecoration: "none",
    "&:hover": {
      fontWeight: "bold",
      color: colors.blue,
      borderBottom: `2px solid ${colors.blue}`,
    },
  },
};

/* An audience tab is "active" while the visitor browses that experience. */
export const audienceTabState = (isActive) => ({
  fontWeight: isActive ? "bold" : "normal",
  borderBottom: isActive ? `2px solid ${colors.blue}` : "none",
});

/* ---------- desktop main bar (white strip) ---------- */

export const mainBar = {
  container: {
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: { xs: "column", md: "row" },
    alignItems: "center",
    flexWrap: "wrap",
    gap: { xs: "10px", xl: "24px" },
    color: colors.blue,
    pt: 2,
    pb: 2,
    bgcolor: colors.white,
    fontSize: 30,
  },
  link: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: colors.navy_blue,
    fontSize: "14px",
    textDecoration: "none",
    transition: "1s",
    "&:hover": {
      color: colors.navy_blue,
    },
  },
};

/* A main-bar entry that opens a dropdown: same as a link, plus the chevron. */
export const dropdownTrigger = (isOpen) => ({
  ...mainBar.link,
  cursor: "pointer",
  "&:hover": { color: colors.navy_blue, cursor: "pointer" },
  "& svg": {
    transition: "1s",
    transform: `rotate(${isOpen ? "-180deg" : "0deg"})`,
  },
});

/* ---------- mobile bar ---------- */

export const mobileBar = {
  container: {
    py: 1,
    bgcolor: colors.dark_grey,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  menuButton: {
    color: colors.blue,
    justifyContent: "start",
    px: 0,
    "&:hover, &:active": { backgroundColor: "transparent" },
  },
  languageButton: {
    color: colors.blue,
    fontWeight: "bold",
    justifyContent: "end",
    px: 0,
    minWidth: 0,
  },
};

/* ---------- dropdown menus ---------- */

export const menuCard = {
  boxShadow: "rgba(0, 0, 0, 0.35) 0px 5px 15px",
  bgcolor: colors.white,
  transition: "1s",
  zIndex: 100,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  overflow: "hidden",
};

export const menuLink = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  color: colors.navy_blue,
  fontSize: 15,
  textDecoration: "none",
  transition: "1s",
  "&:hover": { color: "#282781" },
};

export const menuSeparator = {
  width: "100%",
  mt: 1,
  mb: 2,
  borderBottom: `1px solid ${colors.light_grey}`,
};

/* ---------- mobile drawer ---------- */

export const drawer = {
  panel: { width: { xs: 300, sm: 300 } },
  audienceTab: mainBar.link,
  sectionContent: { display: "flex", flexWrap: "wrap", gap: 1, m: "7px" },
};

export const drawerAudienceTabState = (isActive) => ({
  fontWeight: isActive ? "bold" : "normal",
  borderBottom: isActive
    ? `1px solid ${colors.blue}`
    : `1px dashed ${colors.blue}`,
  padding: "10px",
});
